import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { colors } from '../../tokens/colors'
import { typography } from '../../tokens/typography'
import { mediaQueries } from '../../tokens/breakpoints'

export const DESKTOP_NAVIGATION_WIDTH = 240
export const DESKTOP_COLLAPSED_NAVIGATION_WIDTH = 90
export const MOBILE_NAVIGATION_HEIGHT = 58

const navItemsAppear = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`

export const NavigationContext = styled.div<{ isCollapsed: boolean }>`
  display: contents;
  --desktop-navigation-width: ${({ isCollapsed }) =>
    isCollapsed
      ? DESKTOP_COLLAPSED_NAVIGATION_WIDTH
      : DESKTOP_NAVIGATION_WIDTH}px;
  --mobile-navigation-height: ${MOBILE_NAVIGATION_HEIGHT}px;
`

export const ContentContainer = styled.div`
  width: auto;
  margin-top: var(--mobile-navigation-height);
  transition: width 0.2s ease-in-out;

  @media ${mediaQueries.large.up} {
    width: calc(100% - var(--desktop-navigation-width));
    margin-top: 0;
    margin-left: var(--desktop-navigation-width);
  }

  @media print {
    width: auto;
    margin: 0;
  }
`

export const NavigationContainer = styled.nav<{
  isMobileOpen: boolean
  isFunder: boolean
  isCollapsed: boolean
}>`
  /* set css var for isFunder */
  --navBackground: ${({ isFunder }) =>
    isFunder ? colors.illustration.gray10 : colors.background.navigation};
  --navItemActive: ${({ isFunder }) =>
    isFunder
      ? colors.interactive.disabledDark
      : colors.background.navigationHover};
  --navItemBackground: ${({ isFunder }) =>
    isFunder ? colors.interactive.disabledDark : colors.background.navigation};
  --navItemBorderColor: ${({ isFunder }) =>
    isFunder ? colors.illustration.gray95 : colors.background.navigationActive};
  --navItemHover: ${({ isFunder }) =>
    isFunder
      ? colors.interactive.disabledDark
      : colors.background.navigationHover};
  --navItemIconColor: ${({ isFunder }) =>
    isFunder ? colors.illustration.gray95 : colors.feedback.infoLight};
  background: var(--navBackground);
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  width: ${({ isCollapsed }) => (isCollapsed ? '90px' : '100vw')};
  height: ${({ isMobileOpen }) =>
    isMobileOpen ? '100dvh' : 'var(--mobile-navigation-height)'};
  transition:
    height 0.2s ease-in-out,
    width 0.2s ease-in-out;
  z-index: 1200;

  @media ${mediaQueries.large.up} {
    width: ${({ isCollapsed }) =>
      isCollapsed ? '90px' : 'var(--desktop-navigation-width)'};
    height: 100vh;
    transition: width 0.2s ease-in-out;
  }

  @media print {
    display: none;
  }
`

export const HeaderContainer = styled.div<{
  isMobileOpen: boolean
  isFunder: boolean
  isCollapsed: boolean
}>`
  display: grid;
  grid-template-columns: ${({ isCollapsed }) =>
    isCollapsed ? '1fr' : 'min-content min-content 1fr'};
  padding: 16px 20px;

  background: ${({ isMobileOpen, isFunder }) => {
    if (isFunder) {
      return 'var(--navBackground)'
    }

    if (isMobileOpen) {
      return colors.interactive.primaryEnabled
    }

    return colors.primary.main
  }};

  transition: background 0.1s ease-in;
  border-bottom: none;

  @media ${mediaQueries.large.up} {
    padding: ${({ isCollapsed }) => (isCollapsed ? '24px 0' : '24px 20px')};
    display: ${({ isCollapsed }) => (isCollapsed ? 'flex' : 'grid')};
    justify-content: ${({ isCollapsed }) =>
      isCollapsed ? 'center' : 'flex-start'};
    align-items: center;
  }
`

export const Hamburger = styled.button`
  cursor: pointer;
  justify-self: end;
  background: none;
  border: none;

  @media ${mediaQueries.large.up} {
    display: none;
  }
`

export const PlanName = styled.p`
  ${typography.down2.regularSans.styles};
  color: ${colors.copy.paragraphLight};
  margin: 0px 8px;
  line-height: 1;
`

export const ItemContainer = styled.div<{
  isMobileOpen: boolean
  isCollapsed: boolean
}>`
  display: ${({ isMobileOpen }) => (isMobileOpen ? 'flex' : 'none')};
  flex-direction: column;
  height: 100%;
  animation: ${navItemsAppear} 0.1s ease-in;
  overflow: auto;
  transition: padding 0.2s ease-in-out;
  padding-bottom: 25.5px;

  @media ${mediaQueries.large.up} {
    justify-content: space-between;
    display: flex;
    padding: ${({ isCollapsed }) => (isCollapsed ? '0' : '0')};
  }
  ::-webkit-scrollbar {
    display: none;
  }
`

export const NavItemGroupContainer = styled.div<{
  isCollapsed?: boolean
}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: ${({ isCollapsed }) => (isCollapsed ? 'center' : 'flex-start')};
`

export const Footer = styled.div<{ isCollapsed?: boolean }>`
  background: var(--navBackground);
  display: none;
  padding: ${({ isCollapsed }) => (isCollapsed ? '10px' : '0')};

  @media ${mediaQueries.large.up} {
    display: block;
    margin-top: auto;
  }
`

export const OrgName = styled.p<{ isCollapsed?: boolean }>`
  ${typography.baseline.semiBoldSans}
  color: ${colors.copy.paragraphLight};
  padding: ${({ isCollapsed }) => (isCollapsed ? '10px 0' : '18px 20px')};
  margin: 0px;
  text-align: ${({ isCollapsed }) => (isCollapsed ? 'center' : 'left')};
  white-space: ${({ isCollapsed }) => (isCollapsed ? 'nowrap' : 'normal')};
  overflow: ${({ isCollapsed }) => (isCollapsed ? 'hidden' : 'visible')};
  text-overflow: ${({ isCollapsed }) => (isCollapsed ? 'ellipsis' : 'clip')};
  max-width: ${({ isCollapsed }) => (isCollapsed ? '70px' : 'none')};
`

export const CollapseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;

  &:hover {
    opacity: 0.8;
  }
`
